import { SimpleEvent } from "@eastsideco/salvo-ts";
import { CartApi } from "./CartApi";
import type { IBaseCart } from "./types/IBaseCart";
import type { ShopifyCartItemInput, CartAttributes, ICartApi, ShopifyCartData } from "./types/ICartApi";
import type { ICartItem } from "./types/ICartItem";
import { CartItem } from "./CartItem";
import { monitorForCartChanges } from "./hooks";

export class BaseCart implements IBaseCart {

  items: ICartItem[] = [];
  attributes: CartAttributes = {};
  note = '';
  token = 'unknown'
  totalPrice = 0;
  totalWeight = 0;
  requiresShipping = false;
  currency = 'GBP';

  _onChange: SimpleEvent<void>  = new SimpleEvent();
  api: ICartApi = new CartApi();

  constructor() {
    monitorForCartChanges(this);
  }

  get onChange(): SimpleEvent<void> {
    return this._onChange;
  }

  private throwError(message: string): never {
    console.error(`Cart Error: ${message}`);
    throw new Error(`Cart Error: ${message}`);
  }

  async addItem(item: ShopifyCartItemInput): Promise<ICartItem> {
    const addedItems = await this.addItems([item]);

    if(addedItems.length === 0) {
      this.throwError('Failed to add item to cart');
    }

    return addedItems[0];
  }

  async addItems(items: ShopifyCartItemInput[]): Promise<ICartItem[]> {
    const addedItems = await this.api.cartAdd({
      items: items.map(item => ({
        quantity: 1,
        ...item
      }))
    });

    const newCartItems = addedItems.items.map(item => new CartItem(this, item));
    
    await this.reload();

    return newCartItems;
  }


  async updateNote(note: string): Promise<void> {
    return this.update(note, null);
  }

  async updateAttributes(attributes: CartAttributes): Promise<void> {
    return this.update(null, attributes);
  }

  async update(note: null | string, attributes: null | CartAttributes): Promise<void> {
    const result = await this.api.cartUpdate({
      note: note ?? undefined,
      attributes: attributes ?? undefined
    });
    this.populate(result);
  }

  async updateItemQuantities(updates: number[]): Promise<void> {

    if(updates.length !== this.items.length) {
      this.throwError('Number of updates must match the number of items in the cart.');
    }

    const result = await this.api.cartUpdate({
      updates
    });

    this.populate(result);
  }
  async clear(): Promise<void> {
    this.populate(await this.api.cartClear());
  }
  async reload(): Promise<void> {
    this.populate(await this.api.cartGet());
  }
  async removeItem(item: ICartItem): Promise<void> {

    const indexInCart = this.items.findIndex(cartItem => cartItem.key === item.key);

    if(indexInCart === -1) {
      this.throwError('Item not found in cart');
    }

    const result = await this.api.cartChange({
      id: item.key,
      quantity: 0
    })
    this.populate(result);
  }
  async changeItem(item: ICartItem, quantity: null | number, properties: null | CartAttributes): Promise<void> {

    const indexInCart = this.items.findIndex(cartItem => cartItem.key === item.key);

    if(indexInCart === -1) {
      this.throwError('Item not found in cart');
    }

    const result = await this.api.cartChange({
      id: item.key,
      quantity: quantity ?? undefined,
      properties: properties ?? undefined
    })
    this.populate(result);
  }

  async notifyCartUpdatedExternally(cart?: ShopifyCartData): Promise<void> {
    if(cart) {
      console.log("Cart was updated externally, updating with provided data.");
      this.populate(cart);
      return;
    }
    console.log("Cart was updated externally, updating.");
    await this.reload();
  }

  populate(data: ShopifyCartData) {
    this.items = data.items.map(item => new CartItem(this, item));
    this.attributes = data.attributes;

    this.token = data.token;
    this.note = data.note;
    this.attributes = data.attributes;
    this.totalPrice = data.total_price;
    this.totalWeight = data.total_weight;
    this.requiresShipping = data.requires_shipping;
    this.currency = data.currency;

    this._onChange.dispatch();
  }
  
}